import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { bulkRegisterChannels } from 'api/line';
import { queryKeys } from 'constants/queryKeys';
import { lineStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type { TBulkRegisterChannelEntry, TBulkRegisterError } from 'types';

type BulkRegisterPhase = 'idle' | 'registering' | 'completed' | 'error';

type BulkRegisterProgress = {
  phase: BulkRegisterPhase;
  totalEntries: number;
  processedEntries: number;
  registered: number;
  skipped: number;
  failed: number;
  errors: TBulkRegisterError[];
  error?: string;
};

const initialProgress: BulkRegisterProgress = {
  phase: 'idle',
  totalEntries: 0,
  processedEntries: 0,
  registered: 0,
  skipped: 0,
  failed: 0,
  errors: [],
};

export function useBulkRegisterChannels() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const strings = lineStrings.management.migration.crawl;
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<BulkRegisterProgress>(initialProgress);
  const abortRef = useRef(false);

  useEffect(() => () => { abortRef.current = true; }, []);

  const start = useCallback(
    async (entries: TBulkRegisterChannelEntry[], organizationId?: number) => {
      if (!user) {
        setProgress({ ...initialProgress, phase: 'error', error: strings.errors.unauthorized });
        return;
      }

      const validEntries = entries.filter((e) => e.channel_id);
      abortRef.current = false;
      setIsRunning(true);
      setProgress({
        ...initialProgress,
        phase: 'registering',
        totalEntries: validEntries.length,
      });

      try {
        const allErrors: TBulkRegisterError[] = [];
        let totalRegistered = 0;
        let totalSkipped = 0;
        let totalFailed = 0;

        for (let i = 0; i < validEntries.length; i += 10) {
          if (abortRef.current) break;

          const chunk = validEntries.slice(i, i + 10);
          const result = await bulkRegisterChannels(chunk, organizationId);
          allErrors.push(...result.errors);
          const nextRegistered = totalRegistered + result.registered;
          const nextSkipped = totalSkipped + result.skipped;
          const nextFailed = totalFailed + result.failed;
          totalRegistered = nextRegistered;
          totalSkipped = nextSkipped;
          totalFailed = nextFailed;

          setProgress((prev) => ({
            ...prev,
            processedEntries: Math.min(i + 10, validEntries.length),
            registered: nextRegistered,
            skipped: nextSkipped,
            failed: nextFailed,
            errors: allErrors,
          }));
        }

        if (abortRef.current) return;
        setProgress((prev) => ({ ...prev, phase: 'completed' }));
        await queryClient.invalidateQueries({ queryKey: [queryKeys.LINE_CHANNEL_ACCOUNTS] });
      } catch (caught) {
        const error = caught instanceof Error ? caught.message : strings.errors.channelRegisterFailed;
        setProgress((prev) => ({ ...prev, phase: 'error', error }));
      } finally {
        setIsRunning(false);
      }
    },
    [user, strings, queryClient],
  );

  const reset = useCallback(() => {
    abortRef.current = true;
    setIsRunning(false);
    setProgress(initialProgress);
  }, []);

  return { start, isRunning, progress, reset };
}
